<script setup lang="ts">
import { computed } from 'vue'

/**
 * The Reeded T: a coin's milled rim with a counter-punched T, one filled shape in
 * `currentColor`. Below 24px the twelve fine notches of the full cut go sub-pixel and the
 * rim reads as a spiked blob, so a compact cut swaps in eight heavier notches, a tighter
 * rim and a larger field - two cuts of one mark, not two marks. `strike` plays the
 * identity animation once (sign-in, first authenticated paint); it is a CSS class so
 * `prefers-reduced-motion` degrades it for free (see styles.css).
 */
const props = defineProps<{ size?: number; strike?: boolean }>()
const size = computed(() => props.size ?? 24)
const compact = computed(() => size.value < 24)
</script>

<template>
  <span
    class="relative inline-flex shrink-0 text-[color:var(--color-text)]"
    :class="strike === true ? 'mark-strike' : undefined"
    :style="{ width: `${size}px`, height: `${size}px` }"
  >
    <span
      v-if="strike === true"
      class="mark-strike-halo pointer-events-none absolute inset-0 -m-2 rounded-full border-2 border-[color:var(--color-accent)] opacity-0"
      aria-hidden="true"
    />
    <svg
      :width="size"
      :height="size"
      viewBox="0 0 40 40"
      role="img"
      aria-label="Taler"
      class="mark-strike-body"
    >
      <template v-if="compact">
        <mask id="ui-mark-reeded-sm">
          <rect width="40" height="40" fill="#000" />
          <circle cx="20" cy="20" r="12.4" fill="#fff" />
          <rect x="12.9" y="13.2" width="14.2" height="3.4" fill="#000" />
          <rect x="18.3" y="13.2" width="3.4" height="13.6" fill="#000" />
        </mask>
        <circle
          cx="20"
          cy="20"
          r="17"
          fill="none"
          stroke="currentColor"
          stroke-width="3.4"
          stroke-dasharray="10.35 2.3"
          stroke-dashoffset="1.15"
        />
        <rect width="40" height="40" fill="currentColor" mask="url(#ui-mark-reeded-sm)" />
      </template>
      <template v-else>
        <mask id="ui-mark-reeded">
          <rect width="40" height="40" fill="#000" />
          <circle cx="20" cy="20" r="12.6" fill="#fff" />
          <rect x="12.9" y="13.4" width="14.2" height="3.1" fill="#000" />
          <rect x="18.45" y="13.4" width="3.1" height="13.4" fill="#000" />
        </mask>
        <circle
          cx="20"
          cy="20"
          r="16.6"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-dasharray="5.2 3.492"
          stroke-dashoffset="1.3"
        />
        <rect width="40" height="40" fill="currentColor" mask="url(#ui-mark-reeded)" />
      </template>
    </svg>
  </span>
</template>

<style scoped>
@keyframes mark-strike-in {
  0% {
    transform: scale(1.9) rotateX(72deg);
    opacity: 0;
    filter: blur(1px);
  }
  55% {
    opacity: 1;
  }
  70% {
    transform: scale(0.94) rotateX(0deg);
    filter: blur(0);
  }
  85% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1) rotateX(0);
    opacity: 1;
  }
}
@keyframes mark-strike-ring {
  0% {
    transform: scale(0.6);
    opacity: 0.55;
  }
  100% {
    transform: scale(1.7);
    opacity: 0;
  }
}
.mark-strike .mark-strike-body {
  animation: mark-strike-in var(--motion-strike) var(--ease-strike) both;
}
.mark-strike .mark-strike-halo {
  animation: mark-strike-ring var(--motion-strike) var(--ease-out) both;
}
@media (prefers-reduced-motion: reduce) {
  .mark-strike .mark-strike-body {
    animation: none;
    opacity: 0;
    animation: mark-strike-fade 150ms linear both;
  }
  .mark-strike .mark-strike-halo {
    animation: none;
    display: none;
  }
  @keyframes mark-strike-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
</style>
